import React from 'react';
import { motion } from 'framer-motion';
interface KofschipMnemonicProps {
  verb: string;
  stem: string;
  lastLetter: string;
  useTE: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function KofschipMnemonic({
  verb,
  stem,
  lastLetter,
  useTE,
  label,
  mode = 'support'
}: KofschipMnemonicProps) {
  const kofschipLetters = ['K', 'O', 'F', 'S', 'C', 'H', 'I', 'P'];
  const isInKofschip = kofschipLetters.includes(lastLetter.toUpperCase());
  // For the "not in kofschip" indicator shown below the grid
  const notInKofschipIndicator = !isInKofschip ? <div className="flex items-center justify-center gap-2 mt-2" data-id="element-7731">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg bg-rose-100 text-rose-600 border-2 border-rose-300 shadow-sm" data-id="element-7732">
        {lastLetter.toUpperCase()}
      </div>
      <span className="text-sm font-semibold text-rose-600" data-id="element-7733">
        ← niet in 't kofschip
      </span>
    </div> : null;
  // ── STIMULUS MODE: compact KOFSCHIP grid + result only ────────────────────
  if (mode === 'stimulus') {
    return <div className="flex flex-col items-center gap-3 py-2" data-id="element-7734">
        {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-7735">
            {label}
          </p>}
        {/* Compact KOFSCHIP grid with color coding */}
        <div className="flex items-center gap-1.5" data-id="element-7736">
          <span className="text-base font-black text-slate-400 mr-1" data-id="element-7737">'t</span>
          {kofschipLetters.map(letter => {
          const isMatch = letter === lastLetter.toUpperCase();
          return <div key={letter} className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg shadow-sm
                  ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-id="element-7738">
                {letter}
              </div>;
        })}
        </div>
        {/* Show the tested letter in red if NOT in kofschip */}
        {notInKofschipIndicator}
        {/* Compact result badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border-2 font-bold text-sm
          ${isInKofschip ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-orange-50 border-orange-300 text-orange-800'}`} data-id="element-7739">
          <span className="font-black text-base" data-id="element-7740">{stem}</span>
          <span className="opacity-60" data-id="element-7741">+</span>
          <span className="font-black text-base" data-id="element-7742">{useTE ? '-te' : '-de'}</span>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel ──────────────────────────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-7743">
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 md:p-8 border-2 border-blue-200 shadow-lg" data-id="element-7744">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-7745">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-7746">
            <span className="text-2xl" data-id="element-7747">⛵</span>
            <div className="text-left" data-id="element-7748">
              <div className="text-sm font-bold text-blue-900" data-id="element-7749">
                {label || "'t KOFSCHIP Regel"}
              </div>
              <div className="text-xs text-blue-600 font-semibold" data-id="element-7750">
                Verleden Tijd Spelling
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: Verb → Stem */}
        <div className="mb-6 bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-7751">
          <div className="flex items-start gap-3" data-id="element-7752">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-7753">
              <span className="text-blue-600 font-bold text-sm" data-id="element-7754">1</span>
            </div>
            <div className="flex-1" data-id="element-7755">
              <h4 className="text-sm font-bold text-blue-900 mb-3" data-id="element-7756">
                Stap 1: Vind de stam
              </h4>
              <div className="flex items-center gap-3 flex-wrap" data-id="element-7757">
                <div className="text-center" data-id="element-7758">
                  <div className="text-xs text-slate-500 font-semibold mb-1" data-id="element-7759">
                    Werkwoord
                  </div>
                  <div className="text-xl font-black text-blue-700 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200" data-id="element-7760">
                    {verb}
                  </div>
                </div>
                <div className="text-2xl text-slate-300" data-id="element-7761">→</div>
                <div className="text-center" data-id="element-7762">
                  <div className="text-xs text-slate-500 font-semibold mb-1" data-id="element-7763">
                    Stam (-en weg)
                  </div>
                  <div className="text-xl font-black text-purple-700 bg-purple-50 px-4 py-2 rounded-lg border border-purple-200" data-id="element-7764">
                    {stem}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Last Letter Check */}
        <div className="mb-6 bg-white/80 rounded-xl p-5 border border-cyan-100 shadow-sm" data-id="element-7765">
          <div className="flex items-start gap-3" data-id="element-7766">
            <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0" data-id="element-7767">
              <span className="text-cyan-600 font-bold text-sm" data-id="element-7768">2</span>
            </div>
            <div className="flex-1" data-id="element-7769">
              <h4 className="text-sm font-bold text-cyan-900 mb-3" data-id="element-7770">
                Stap 2: Kijk naar de laatste letter van de stam
              </h4>
              <div className="text-center" data-id="element-7771">
                <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 ${isInKofschip ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-7772">
                  <span className="text-sm text-slate-600 font-semibold" data-id="element-7773">
                    Laatste letter:
                  </span>
                  <span className={`text-3xl font-black ${isInKofschip ? 'text-emerald-700' : 'text-rose-600'}`} data-id="element-7774">
                    {lastLetter.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: KOFSCHIP Check — with color coding */}
        <div className="mb-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border-2 border-indigo-200 shadow-md" data-id="element-7775">
          <div className="flex items-start gap-3 mb-4" data-id="element-7776">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-7777">
              <span className="text-indigo-600 font-bold text-sm" data-id="element-7778">3</span>
            </div>
            <div data-id="element-7779">
              <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-7780">
                Stap 3: Staat de letter in 't KOFSCHIP?
              </h4>
              <p className="text-xs text-slate-600 italic" data-id="element-7781">
                't KOFSCHIP is een ezelsbruggetje om te onthouden welke letters
                -te krijgen
              </p>
            </div>
          </div>
          {/* Color-coded KOFSCHIP grid */}
          <div className="flex justify-center gap-2 mb-3" data-id="element-7782">
            <div className="text-2xl font-black text-slate-400 mr-2" data-id="element-7783">'t</div>
            {kofschipLetters.map((letter, index) => {
            const isMatch = letter === lastLetter.toUpperCase();
            return <motion.div key={letter} initial={{
              scale: 0,
              rotate: -10
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              delay: index * 0.05,
              type: 'spring',
              stiffness: 300
            }} className={`w-12 h-12 rounded-lg flex items-center justify-center font-black text-2xl transition-all duration-300 shadow-md
                    ${isMatch ? 'bg-gradient-to-br from-green-400 to-emerald-500 text-white ring-4 ring-green-200 scale-110' : 'bg-emerald-50 text-emerald-700 border-2 border-emerald-200'}`} data-id="element-7784">
                  {letter}
                </motion.div>;
          })}
          </div>
          {/* Show tested letter in red if NOT in kofschip */}
          {!isInKofschip && <div className="flex items-center justify-center gap-2 mb-4" data-id="element-7785">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.4,
            type: 'spring',
            stiffness: 300
          }} className="w-12 h-12 rounded-lg flex items-center justify-center font-black text-2xl bg-rose-100 text-rose-600 border-2 border-rose-300 shadow-md" data-id="element-7786">
                {lastLetter.toUpperCase()}
              </motion.div>
              <motion.span initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.5
          }} className="text-sm font-bold text-rose-600" data-id="element-7787">
                ← zit er NIET in!
              </motion.span>
            </div>}
          <div className={`text-center p-4 rounded-xl border-2 font-bold ${isInKofschip ? 'bg-green-50 border-green-200 text-green-800' : 'bg-orange-50 border-orange-200 text-orange-800'}`} data-id="element-7788">
            {isInKofschip ? <div data-id="element-7789">
                <div className="text-lg mb-1" data-id="element-7790">
                  ✅ Ja! De letter{' '}
                  <span className="text-2xl" data-id="element-7791">{lastLetter.toUpperCase()}</span>{' '}
                  zit in 't KOFSCHIP
                </div>
                <div className="text-sm" data-id="element-7792">
                  Dus: verleden tijd krijgt <strong data-id="element-7793">-te</strong> (of{' '}
                  <strong data-id="element-7794">-tte</strong> bij meervoud)
                </div>
              </div> : <div data-id="element-7795">
                <div className="text-lg mb-1" data-id="element-7796">
                  ❌ Nee! De letter{' '}
                  <span className="text-2xl" data-id="element-7797">{lastLetter.toUpperCase()}</span>{' '}
                  zit NIET in 't KOFSCHIP
                </div>
                <div className="text-sm" data-id="element-7798">
                  Dus: verleden tijd krijgt <strong data-id="element-7799">-de</strong> (of{' '}
                  <strong data-id="element-7800">-dde</strong> bij meervoud)
                </div>
              </div>}
          </div>
        </div>

        <div className="space-y-4" data-id="element-7801">
          <div className="bg-white/80 rounded-xl p-5 border border-emerald-100 shadow-sm" data-id="element-7802">
            <div className="flex items-start gap-3" data-id="element-7803">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-7804">
                <span className="text-emerald-600 text-lg" data-id="element-7805">📝</span>
              </div>
              <div data-id="element-7806">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-7807">
                  Voorbeeld met dit werkwoord
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-7808">
                  <p data-id="element-7809">
                    <strong data-id="element-7810">Stam:</strong> {stem}
                  </p>
                  <p data-id="element-7811">
                    <strong data-id="element-7812">Laatste letter:</strong> {lastLetter.toUpperCase()}
                    {isInKofschip ? ' (zit in KOFSCHIP ✓)' : ' (zit NIET in KOFSCHIP ✗)'}
                  </p>
                  <p data-id="element-7813">
                    <strong data-id="element-7814">Verleden tijd:</strong>{' '}
                    <span className="font-semibold text-emerald-700" data-id="element-7815">
                      {stem}
                      {useTE ? 'te' : 'de'}
                    </span>{' '}
                    (enkelvoud) /{' '}
                    <span className="font-semibold text-emerald-700" data-id="element-7816">
                      {stem}
                      {useTE ? 'tten' : 'dden'}
                    </span>{' '}
                    (meervoud)
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-7817">
            <div className="flex items-start gap-3" data-id="element-7818">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-7819">
                <span className="text-amber-600 text-lg" data-id="element-7820">💡</span>
              </div>
              <div data-id="element-7821">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-7822">
                  Ezelsbruggetje
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-7823">
                  Onthoud: <strong data-id="element-7824">'t KOFSCHIP</strong> vaart door de haven. Als
                  de laatste letter van de stam in dit woord zit, gebruik je{' '}
                  <strong data-id="element-7825">-te</strong>. Anders gebruik je <strong data-id="element-7826">-de</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}